import React from 'react';
import { Box } from '@mui/material';

const WIDTH = 320;
const HEIGHT = 150;
const PAD_L = 6;
const PAD_R = 6;
const PAD_T = 10;
const PAD_B = 20;

/**
 * MiniLineChart — a compact SVG line chart for the rolling-redemption series
 * (mockup screen 9). Mirrors the desktop visx LineChart: X is the point index
 * ordered by date, Y is a percentage. Draws an optional dashed average
 * reference line, and — when `onSelect` is given — a tappable marker per point
 * that reports the selected index (for a details panel). `points: [{ label,
 * value }]` where value is a percentage.
 */
const MiniLineChart = function MiniLineChart({
  points = [],
  color = '#1D5A9D',
  averageValue = null,
  onSelect = null,
  selectedIndex = null,
}) {
  const innerW = WIDTH - PAD_L - PAD_R;
  const innerH = HEIGHT - PAD_T - PAD_B;
  const maxY = Math.max(10, ...points.map((p) => p.value || 0));
  const count = points.length;

  const xAt = (index) =>
    count <= 1 ? PAD_L + innerW / 2 : PAD_L + (index / (count - 1)) * innerW;
  const yAt = (value) => PAD_T + innerH - ((value || 0) / maxY) * innerH;

  const line = points.map((p, i) => `${xAt(i)},${yAt(p.value)}`).join(' ');
  const labelStep = Math.max(1, Math.ceil(count / 4));
  const avgY = averageValue != null ? yAt(Math.min(maxY, averageValue)) : null;

  return (
    <Box sx={{ width: '100%' }}>
      <svg
        width="100%"
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
      >
        {[0, 0.5, 1].map((ratio) => (
          <line
            key={ratio}
            x1={PAD_L}
            x2={WIDTH - PAD_R}
            y1={PAD_T + innerH * ratio}
            y2={PAD_T + innerH * ratio}
            stroke="#eef1f6"
            strokeWidth="1"
          />
        ))}
        {avgY != null ? (
          <line
            x1={PAD_L}
            x2={WIDTH - PAD_R}
            y1={avgY}
            y2={avgY}
            stroke={color}
            strokeWidth="1.5"
            strokeDasharray="4 3"
            opacity="0.6"
          />
        ) : null}
        {selectedIndex != null && points[selectedIndex] ? (
          <line
            x1={xAt(selectedIndex)}
            x2={xAt(selectedIndex)}
            y1={PAD_T}
            y2={PAD_T + innerH}
            stroke={color}
            strokeWidth="1"
            opacity="0.35"
          />
        ) : null}
        {count > 1 ? (
          <polyline
            points={line}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={xAt(i)}
            cy={yAt(p.value)}
            r={selectedIndex === i ? 4 : 2.5}
            fill={color}
          />
        ))}
        {points.map((p, i) =>
          i % labelStep === 0 || i === count - 1 ? (
            <text
              key={`l-${i}`}
              x={xAt(i)}
              y={HEIGHT - 6}
              textAnchor="middle"
              fontSize="9"
              fill="#94a3b8"
            >
              {p.label}
            </text>
          ) : null
        )}
        {onSelect && count
          ? points.map((p, i) => {
              const half = count <= 1 ? innerW / 2 : innerW / (count - 1) / 2;
              return (
                <rect
                  key={`hit-${i}`}
                  x={xAt(i) - half}
                  y={PAD_T}
                  width={half * 2}
                  height={innerH}
                  fill="transparent"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onSelect(i)}
                />
              );
            })
          : null}
      </svg>
    </Box>
  );
};

export default MiniLineChart;
